// =============================================================================
// ENUMS
// =============================================================================
/**
 * Risk Status - Workflow stages (does NOT affect risk score calculations)
 */
export var RiskStatus;
(function (RiskStatus) {
    RiskStatus["IDENTIFIED"] = "identified";
    RiskStatus["ASSESSED"] = "assessed";
    RiskStatus["TREATMENT_PLANNED"] = "treatment_planned";
    RiskStatus["MITIGATED"] = "mitigated";
    RiskStatus["ACCEPTED"] = "accepted";
    RiskStatus["CLOSED"] = "closed";
})(RiskStatus || (RiskStatus = {}));
/**
 * Risk Treatment Strategy (ISO 27005)
 */
export var RiskTreatment;
(function (RiskTreatment) {
    RiskTreatment["MITIGATE"] = "mitigate";
    RiskTreatment["ACCEPT"] = "accept";
    RiskTreatment["TRANSFER"] = "transfer";
    RiskTreatment["AVOID"] = "avoid";
})(RiskTreatment || (RiskTreatment = {}));
/**
 * Mitigation Status - Only 'implemented' and 'verified' reduce risk scores
 */
export var MitigationStatus;
(function (MitigationStatus) {
    MitigationStatus["PLANNED"] = "planned";
    MitigationStatus["IN_PROGRESS"] = "in_progress";
    MitigationStatus["IMPLEMENTED"] = "implemented";
    MitigationStatus["VERIFIED"] = "verified";
})(MitigationStatus || (MitigationStatus = {}));
/**
 * Control Type (ISO 27001:2022)
 */
export var ControlType;
(function (ControlType) {
    ControlType["PREVENTIVE"] = "preventive";
    ControlType["DETECTIVE"] = "detective";
    ControlType["CORRECTIVE"] = "corrective";
    ControlType["COMPENSATING"] = "compensating";
})(ControlType || (ControlType = {}));
/**
 * Suggestion Priority - Affects UI color coding
 * LOW = Green (#42B8B2 mintblå)
 * MEDIUM = Yellow (#FFBB00 okergul)
 * HIGH = Red (#FE3B00 orange)
 */
export var SuggestionPriority;
(function (SuggestionPriority) {
    SuggestionPriority["LOW"] = "low";
    SuggestionPriority["MEDIUM"] = "medium";
    SuggestionPriority["HIGH"] = "high";
})(SuggestionPriority || (SuggestionPriority = {}));
// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
/**
 * Calculate risk level from score (0-10 scale)
 */
export function getRiskLevel(score) {
    if (score >= 8.0)
        return 'critical';
    if (score >= 6.0)
        return 'high';
    if (score >= 4.0)
        return 'medium';
    if (score >= 2.0)
        return 'low';
    return 'minimal';
}
/**
 * Get risk level color (Avinor brand colors)
 */
export function getRiskLevelColor(level) {
    switch (level) {
        case 'critical':
            return '#FE3B00'; // orange
        case 'high':
            return '#692164'; // fiolett
        case 'medium':
            return '#FFBB00'; // okergul
        case 'low':
            return '#42B8B2'; // mintblå
        case 'minimal':
            return '#939597'; // grå04
    }
}
/**
 * Get suggestion priority color (Avinor brand colors)
 */
export function getSuggestionPriorityColor(priority) {
    switch (priority) {
        case SuggestionPriority.HIGH:
            return '#FE3B00'; // orange - red
        case SuggestionPriority.MEDIUM:
            return '#FFBB00'; // okergul - yellow
        case SuggestionPriority.LOW:
            return '#42B8B2'; // mintblå - green
    }
}
/**
 * Calculate inherent risk score
 * Formula: ((C + I + A) / 3) × likelihood / 10
 */
export function calculateInherentRisk(confidentialityImpact, integrityImpact, availabilityImpact, likelihood) {
    const avgImpact = (confidentialityImpact + integrityImpact + availabilityImpact) / 3;
    const inherentRisk = (avgImpact * likelihood) / 10;
    return parseFloat(inherentRisk.toFixed(2));
}
/**
 * Calculate residual risk after mitigations
 * Formula: inherentRisk × (1 - totalReductionFactor)
 * Uses multiplicative reduction for multiple mitigations
 */
export function calculateResidualRisk(inherentRisk, reductionFactors) {
    let remainingRisk = 1.0;
    for (const factor of reductionFactors) {
        remainingRisk *= 1 - factor;
    }
    const residualRisk = inherentRisk * remainingRisk;
    return parseFloat(residualRisk.toFixed(2));
}
